import type { EffectTimelineClip, RenderMode, VideoEffect, VideoFrame } from './types';
import { DEFAULT_EFFECT_ORDER } from './registry';
import { clipsToVideoEffects } from './serialization';
import { buildRenderContext, cloneVideoFrame } from './frameUtils';

export interface RenderGraphOptions {
  fps?: number;
  mode?: RenderMode;
  seed?: number;
  /** Override default effect execution order. */
  order?: readonly string[];
}

/**
 * Ordered effect stack:
 * Video Frame → Color Grade → Glow → Motion Blur → Lens Flare → Glitch → Output
 */
export class RenderGraph {
  private readonly effects: VideoEffect[];
  private readonly order: readonly string[];
  private readonly frameHistory: VideoFrame[] = [];
  private readonly maxHistory: number;

  constructor(effects: VideoEffect[], order: readonly string[] = DEFAULT_EFFECT_ORDER, maxHistory = 4) {
    this.order = order;
    this.maxHistory = maxHistory;
    this.effects = [...effects].sort((a, b) => {
      const ia = order.indexOf(a.type as typeof order[number]);
      const ib = order.indexOf(b.type as typeof order[number]);
      const ai = ia === -1 ? 999 : ia;
      const bi = ib === -1 ? 999 : ib;
      if (ai !== bi) return ai - bi;
      return a.startTime - b.startTime;
    });
  }

  static fromClips(clips: EffectTimelineClip[], opts?: RenderGraphOptions): RenderGraph {
    const effects = clipsToVideoEffects(clips.filter((c) => c.enabled !== false));
    return new RenderGraph(effects, opts?.order ?? DEFAULT_EFFECT_ORDER);
  }

  /** Effects active at timeline time. */
  activeAt(time: number): VideoEffect[] {
    return this.effects.filter(
      (e) => e.enabled && time >= e.startTime && time < e.endTime,
    );
  }

  /** Keep runtime effect instances aligned with latest timeline clip state. */
  syncFromClips(clips: EffectTimelineClip[]): void {
    const map = new Map(
      (clips || []).filter((c) => c.enabled !== false).map((c) => [c.id, c]),
    );
    for (const effect of this.effects) {
      const clip = map.get(effect.id);
      if (!clip) continue;
      effect.startTime = clip.startTime;
      effect.endTime = clip.endTime;
      effect.enabled = clip.enabled !== false;
      effect.parameters = { ...(clip.parameters || {}) };
      effect.keyframeTracks = clip.keyframeTracks;
    }
  }

  renderFrame(
    input: VideoFrame,
    currentTime: number,
    frameIndex: number,
    opts: RenderGraphOptions = {},
  ): VideoFrame {
    const fps = opts.fps ?? 30;
    const mode = opts.mode ?? 'preview';
    const seed = opts.seed ?? 0;
    const active = this.activeAt(currentTime);
    if (!active.length) return input;

    let frame = input;
    const previous = [...this.frameHistory];

    for (const effect of active) {
      const ctx = buildRenderContext(
        currentTime,
        frameIndex,
        fps,
        mode,
        previous,
        effect.startTime,
        effect.endTime,
        seed,
      );
      try {
        const next = effect.render(frame, ctx);
        if (next?.canvas) frame = next;
      } catch {
        // Skip failed effect; continue rendering the rest of the stack.
      }
    }

    this.frameHistory.unshift(cloneVideoFrame(frame));
    if (this.frameHistory.length > this.maxHistory) {
      this.frameHistory.pop();
    }
    return frame;
  }

  resetHistory(): void {
    this.frameHistory.length = 0;
  }
}

export function renderEffectStack(
  input: VideoFrame,
  clips: EffectTimelineClip[],
  currentTime: number,
  frameIndex: number,
  opts?: RenderGraphOptions,
): VideoFrame {
  const graph = RenderGraph.fromClips(clips, opts);
  return graph.renderFrame(input, currentTime, frameIndex, opts);
}
